import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, btnGhost, btnGold, fmtErr, input, label } from "./shared";

export const bankLabel = (b) => `${b.bank_name} — A/c (${(b.account_no || "").slice(-5)})${b.branch ? ` · ${b.branch}` : ""}`;

/** "Paid From Bank" dropdown — company bank accounts managed by the CEO under Salary → Settings. */
export const BankSelect = ({ value, onChange, testId = "paid-from-bank", className }) => {
  const [banks, setBanks] = useState(null);
  useEffect(() => { api.get("/salary/company-banks").then((r) => setBanks(r.data.items)).catch(() => setBanks([])); }, []);
  return (
    <label className={`text-[10px] uppercase tracking-wider text-slate-400 ${className || ""}`}>Paid from bank <span className="text-amber-300">*</span>
      <select required value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 block w-full bg-[#0b1220] border border-white/15 text-slate-100 px-3 py-2 text-sm" style={{ colorScheme: "dark" }} data-testid={testId}>
        <option value="">{banks === null ? "Loading…" : banks.length ? "Select company bank account…" : "No company bank added — add one under Settings"}</option>
        {(banks || []).map((b) => <option key={b.id} value={b.id} className="bg-[#0b1220] text-slate-100">{bankLabel(b)}</option>)}
      </select>
    </label>
  );
};

const EMPTY = { bank_name: "", holder_name: "", account_no: "", ifsc: "", branch: "", branch_address: "", city: "", state: "", remarks: "" };
const FIELDS = [["bank_name", "Bank name", true], ["holder_name", "Account holder name", true], ["account_no", "Account number", true], ["ifsc", "IFSC code", true], ["branch", "Branch name"], ["branch_address", "Branch address"], ["city", "City"], ["state", "State"], ["remarks", "Remarks"]];

const BankForm = ({ initial, onClose, onSaved }) => {
  const [b, setB] = useState({ ...EMPTY, ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = initial?.id ? await api.put(`/salary/company-banks/${initial.id}`, b) : await api.post("/salary/company-banks", b);
      toast.success(initial?.id ? "Bank account updated" : "Company bank account added"); onSaved(data);
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="bank-form-dialog">
      <form onSubmit={save} className="w-full max-w-lg bg-nx-navy2 border border-white/10 p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2"><h3 className="text-white font-marcellus text-xl flex-1">{initial?.id ? "Edit bank account" : "Add company bank account"}</h3><button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-white" data-testid="bank-form-close"><X size={18} /></button></div>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, l, req]) => (
            <div key={k} className={k === "branch_address" || k === "remarks" ? "sm:col-span-2" : ""}><label className={label}>{l}{req && <span className="text-amber-300"> *</span>}</label><input required={!!req} value={b[k]} onChange={(e) => setB({ ...b, [k]: e.target.value })} className={`${input} ${k === "account_no" || k === "ifsc" ? "font-mono" : ""}`} data-testid={`bank-field-${k}`} /></div>
          ))}
        </div>
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className={btnGhost}>Cancel</button><button disabled={busy} className={btnGold} data-testid="bank-form-save">{busy ? <Loader2 size={13} className="animate-spin" /> : <Landmark size={13} />} {initial?.id ? "Save changes" : "Add bank"}</button></div>
      </form>
    </div>
  );
};

/** CEO: manage the company bank accounts salaries are paid from (Salary → Settings). */
export const CompanyBanks = ({ isCeo }) => {
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/salary/company-banks").then((r) => setBanks(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (b) => {
    if (!window.confirm(`Remove ${b.bank_name} A/c ${b.account_no}? Past payments keep their recorded bank details.`)) return;
    try { await api.delete(`/salary/company-banks/${b.id}`); toast.success("Bank account removed"); load(); } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="company-banks">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Landmark size={15} className="text-nx-goldlight" /><h3 className="text-white text-sm flex-1">Company bank accounts <span className="text-slate-500 text-xs">— selected as "Paid from bank" when marking salaries paid</span></h3>
        {isCeo && <button onClick={() => setForm({})} className={btnGold} data-testid="bank-add"><Plus size={13} /> Add bank account</button>}
      </div>
      {banks.length === 0 ? <p className="text-xs text-slate-500" data-testid="company-banks-empty">No company bank account added yet.{isCeo ? " Add one to enable Mark as paid." : ""}</p> : (
        <div className="grid md:grid-cols-2 gap-2">
          {banks.map((b) => (
            <div key={b.id} className="border border-white/10 p-3 text-xs" data-testid={`bank-card-${b.id}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-slate-100 text-sm">{b.bank_name} <span className="text-slate-500">· {b.holder_name}</span></div>
                  <div className="font-mono text-nx-goldlight mt-0.5" data-testid={`bank-account-${b.id}`}>A/c {b.account_no} · IFSC {b.ifsc}</div>
                  <div className="text-slate-500 mt-0.5">Shown to staff as: NexSecure Technologies - {b.bank_name} A/c ({(b.account_no || "").slice(-5)})</div>
                  <div className="text-slate-400 mt-1">{[b.branch, b.branch_address, b.city, b.state].filter(Boolean).join(", ") || "—"}</div>
                  {b.remarks && <div className="text-slate-500 mt-1 italic">{b.remarks}</div>}
                </div>
                {isCeo && <div className="flex gap-1 shrink-0"><button onClick={() => setForm(b)} className="p-1.5 text-slate-400 hover:text-white" title="Edit" data-testid={`bank-edit-${b.id}`}><Pencil size={14} /></button><button onClick={() => del(b)} className="p-1.5 text-slate-500 hover:text-red-300" title="Remove" data-testid={`bank-delete-${b.id}`}><Trash2 size={14} /></button></div>}
              </div>
            </div>
          ))}
        </div>
      )}
      {form && <BankForm initial={form.id ? form : null} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
};
